import { Link } from 'react-router-dom';
import { mediaAsset } from '../config/media';

function StudySection({ eyebrow, title, children, className = '' }) {
  return (
    <section className={`remote-study__section ${className}`.trim()}>
      <div className="remote-study__heading">
        <p className="portfolio-eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
      </div>
      <div className="remote-study__body">{children}</div>
    </section>
  );
}

function LoopNode({ label, detail }) {
  return (
    <div className="mf1-loop__node">
      <strong>{label}</strong>
      <span>{detail}</span>
    </div>
  );
}

function FeedbackLoopDiagram() {
  return (
    <figure className="mf1-loop" aria-labelledby="mf1-loop-caption">
      <div className="mf1-loop__path">
        <p>Command path</p>
        <div className="mf1-loop__flow mf1-loop__flow--command">
          <LoopNode label="DAW" detail="Automation + recall" />
          <i aria-hidden="true">→</i>
          <LoopNode label="MIDI" detail="Target value" />
          <i aria-hidden="true">→</i>
          <LoopNode label="Teensy controller" detail="Firmware + position control" />
          <i aria-hidden="true">→</i>
          <LoopNode label="Motor driver" detail="Power + direction" />
          <i aria-hidden="true">→</i>
          <LoopNode label="Motorized fader" detail="Physical movement" />
        </div>
      </div>

      <div className="mf1-loop__turn" aria-hidden="true">Position feedback closes the loop</div>

      <div className="mf1-loop__path">
        <p>Feedback path</p>
        <div className="mf1-loop__flow mf1-loop__flow--feedback">
          <LoopNode label="Fader position" detail="Touch or motor movement" />
          <i aria-hidden="true">→</i>
          <LoopNode label="Teensy controller" detail="Read + compare" />
          <i aria-hidden="true">→</i>
          <LoopNode label="MIDI" detail="Position update" />
          <i aria-hidden="true">→</i>
          <LoopNode label="DAW" detail="Mix state synchronized" />
        </div>
      </div>

      <figcaption id="mf1-loop-caption">The DAW sets a target position; the fader reports its physical position back so the software and control surface remain synchronized.</figcaption>
    </figure>
  );
}

export default function Mf1CaseStudy() {
  return (
    <main id="main-content" className="portfolio-page remote-study mf1-study">
      <header className="portfolio-page-intro remote-study__intro">
        <p className="portfolio-eyebrow">Hardware · MIDI · Full-stack prototyping</p>
        <h1>MF-1</h1>
        <p>A compact motorized-fader controller for music production.</p>
      </header>

      <section className="case-study-summary remote-study__summary" aria-label="MF-1 project summary">
        <div>
          <p className="portfolio-eyebrow">Role</p>
          <p>Product Designer and Engineer</p>
        </div>
        <div>
          <p className="portfolio-eyebrow">Period</p>
          <p>2019</p>
        </div>
        <div>
          <p className="portfolio-eyebrow">Focus</p>
          <p>Embedded systems, PCB design, MIDI, interaction design, and digital fabrication</p>
        </div>
      </section>

      <figure className="case-study-hero mf1-study__hero">
        <img
          src={mediaAsset('images/mf-1/mf-1-angle.jpeg')}
          alt="Completed MF-1 controller with eight motorized faders, a joystick, and an illuminated control"
        />
        <figcaption>The completed MF-1 prototype in my music-production workspace.</figcaption>
      </figure>

      <section className="remote-study__metrics mf1-study__layers" aria-label="Five integrated layers of MF-1">
        <article><strong>Product design</strong><span>Concept, interaction model, and physical form</span></article>
        <article><strong>Electronics</strong><span>Components, schematics, power delivery, and custom PCB</span></article>
        <article><strong>Firmware</strong><span>Motor control, position feedback, and embedded state</span></article>
        <article><strong>MIDI integration</strong><span>DAW communication, automation, and recall</span></article>
        <article><strong>Fabrication</strong><span>CNC enclosure, assembly, and mechanical tolerances</span></article>
      </section>

      <StudySection eyebrow="Context" title="Responsive control without the console footprint">
        <p>
          Most affordable MIDI controllers use conventional faders. Change a value in the software and the physical control does not follow it. Switch tracks or reopen a project and its position no longer represents the value on screen.
        </p>
        <p>
          Motorized faders can recall a saved mix, follow automation, and update with the selected track or parameter. The faders I selected also offered finer positional resolution and smoother travel than the simple linear potentiometers in many lower-cost controllers. That made gradual level changes more precise and fluid.
        </p>
        <p>
          Commercial systems with those capabilities are generally expensive, large, and organized around fixed studio-console layouts. I wanted to test whether the essential interaction could be delivered with a lower cost, smaller footprint, and less weight.
        </p>
      </StudySection>

      <StudySection eyebrow="Ownership" title="Designing the complete system" className="remote-study__section--dark">
        <p>
          I designed and built the entire prototype: the product concept, interaction model, component selection, schematics, PCB, embedded firmware, MIDI behavior, enclosure, fabrication, assembly, and testing.
        </p>
        <p>
          Each layer had to work as part of one feedback system. The firmware received MIDI from the computer, determined the requested position, drove the motor, read the fader, and returned the user's movement to the DAW. The electronics needed to supply enough current without making the controller unnecessarily large or expensive. The enclosure had to hold the boards and mechanical parts precisely enough for the faders to move reliably.
        </p>
        <FeedbackLoopDiagram />
      </StudySection>

      <StudySection eyebrow="Technical challenge" title="Powering motors at prototype scale">
        <p>
          Powering the fader motors was the hardest hardware constraint. I needed compact integrated circuits capable of driving them within the controller's size and power requirements. Many suitable components were difficult to obtain without purchasing production quantities in the hundreds or thousands.
        </p>
        <p>
          Part selection required balancing current capacity, thermal behavior, board area, availability, price, packaging, and minimum-order quantities. Component availability became an architectural constraint that shaped both the PCB and the physical product.
        </p>
        <div className="mf1-study__technical-gallery">
          <figure>
            <img
              src={mediaAsset('images/mf-1/mf-1-pcb.jpeg')}
              alt="Unpopulated custom MF-1 circuit board showing the Teensy and motor-driver layout"
              loading="lazy"
            />
            <figcaption>The custom PCB before assembly, with footprints for the Teensy microcontroller, motor drivers, and eight faders.</figcaption>
          </figure>
          <figure>
            <img
              src={mediaAsset('images/mf-1/mf-1-electronics.jpeg')}
              alt="MF-1 motor-driver electronics visible beneath the transparent top panel"
              loading="lazy"
            />
            <figcaption>Motor-driver electronics and wiring beneath the controller surface.</figcaption>
          </figure>
        </div>
      </StudySection>

      <StudySection eyebrow="Hardware + firmware" title="PCB, embedded code, and enclosure" className="remote-study__split">
        <div>
          <p>
            I designed the schematics and circuit boards in EAGLE. Working within the embedded system's memory and processing limits forced me to write concise firmware and make deliberate decisions about how state, control messages, and fader updates were represented.
          </p>
          <p>
            Embedded mistakes could appear as mechanical instability, timing problems, electrical noise, excessive current draw, or different behavior under load. Debugging meant treating code, electronics, and the physical mechanism as one problem.
          </p>
          <p>
            I designed the enclosure and fabricated it with CNC machining. Component clearances, material thickness, mounting points, cable routing, tool access, tolerances, and serviceability all affected the interface and final layout.
          </p>
        </div>
        <figure className="mf1-study__detail">
          <img
            src={mediaAsset('images/mf-1/mf-1-front.jpeg')}
            alt="Front view of the assembled MF-1 motorized-fader controller"
            loading="lazy"
          />
          <figcaption>The assembled enclosure and control surface.</figcaption>
        </figure>
      </StudySection>

      <StudySection eyebrow="Outcome" title="Prototype results" className="remote-study__section--accent">
        <p>
          MF-1 demonstrated that a responsive motorized controller could be built with a lower cost, smaller footprint, and less weight than the commercial alternatives I evaluated. It retained the tactile advantages that mattered for mixing: smooth travel, fine positional control, automation feedback, and recall. I achieved those results in a one-off prototype, before potential savings from manufacturing at scale.
        </p>
        <p>
          The prototype did not establish the size of the market. Many producers were accustomed to nonresponsive controls and did not necessarily see motorized feedback as an urgent problem. Mix engineers and professional studios presented the clearer opportunity, where reduced cost, weight, and footprint addressed practical constraints.
        </p>
        <p>
          The controller remains functional, and I still use it in music-production and mixing workflows. A second version would need LCD displays and companion software so each fader could be labeled dynamically for the current track, parameter, and project.
        </p>
      </StudySection>

      <StudySection eyebrow="Reflection" title="Successful prototype, unproven market" className="remote-study__closing">
        <p>
          MF-1 taught me to see firmware, power delivery, component sourcing, PCB layout, mechanical tolerances, and fabrication as one connected system. A software decision could affect motor behavior; a part change could alter the board; a connector could reshape the enclosure.
        </p>
        <p>
          Selecting the ideal component on paper was not enough when it was unavailable at a realistic quantity. I had to design around the supply chain available to a one-off prototype, not the one that might exist after funding or mass production.
        </p>
        <p>
          The prototype established technical feasibility and improved cost, weight, footprint, and control. It did not establish whether enough customers would change their workflows to adopt it. The next step would have been targeted validation with professional users.
        </p>
        <p>
          I completed MF-1 shortly before COVID-19 changed professional and creative work. I shifted my attention toward software and remote collaboration rather than taking the hardware into production.
        </p>
        <aside className="remote-study__boundary">
          <p className="portfolio-eyebrow">Attribution</p>
          <p>
            MF-1 was an independent project. I designed and built the concept, electronics, PCB, firmware, MIDI behavior, enclosure, fabrication, assembly, and testing.
          </p>
        </aside>
        <div className="portfolio-actions">
          <Link className="portfolio-button" to="/contact">Start a conversation</Link>
          <Link className="portfolio-text-link" to="/work">Return to selected work</Link>
        </div>
      </StudySection>
    </main>
  );
}
